import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BadgeCheck } from "lucide-react";
import api from "../api";
import { useLang, fmtPrice, statusKey } from "../i18n";
import AcquirePanel from "../components/AcquirePanel";

export default function ArtworkDetail() {
  const { slug } = useParams();
  const { t, loc, lang } = useLang();
  const [data, setData] = useState(null);
  const [acquire, setAcquire] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");

  useEffect(() => {
    setData(null);
    api.get(`/artworks/${slug}`).then((r) => setData(r.data)).catch(() => {});
  }, [slug]);

  if (!data)
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center font-mono-meta text-xs uppercase tracking-[0.2em] text-[#8C8983]">
        …
      </div>
    );

  const w = data.artwork;
  const rows = [
    [t("detail.year"), w.year],
    [t("detail.technique"), loc(w, "technique")],
    [t("detail.support"), loc(w, "support")],
    [t("detail.dimensions"), w.dimensions],
    [t("detail.series"), loc(w, "series")],
  ];

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };

  return (
    <div data-testid="artwork-detail-page" className="pt-16">
      <div className="px-6 sm:px-12 lg:px-20 py-6">
        <Link
          to="/oeuvres"
          data-testid="detail-back-link"
          className="inline-flex items-center gap-2 font-mono-meta text-[11px] uppercase tracking-[0.18em] text-[#8C8983] hover:text-[#121212] transition-colors"
        >
          <ArrowLeft size={13} /> {t("detail.back")}
        </Link>
      </div>

      <div className="px-6 sm:px-12 lg:px-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div
            className="zoomable overflow-hidden cursor-crosshair p-4 bg-white frame-shadow"
            onMouseMove={onMove}
            data-testid="detail-image-frame"
          >
            <img
              src={w.image}
              alt={loc(w, "title")}
              className="zoom-img w-full max-h-[75vh] object-contain"
              style={{ transformOrigin: origin }}
            />
          </div>
          <p className="mt-4 font-mono-meta text-[10px] uppercase tracking-[0.2em] text-[#8C8983]">{t("works.hint")}</p>
        </motion.div>

        {/* Dossier */}
        <motion.aside
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#C83E2B] mb-4" data-testid="detail-series">
            {loc(w, "series")} — {w.year}
          </p>
          <h1 className="font-serif-display font-light text-5xl lg:text-6xl leading-[1.02] mb-10" data-testid="detail-title">
            {loc(w, "title")}
          </h1>

          <dl className="border-t" style={{ borderColor: "#E5E2DC" }} data-testid="detail-dossier">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 py-4 border-b" style={{ borderColor: "#E5E2DC" }}>
                <dt className="font-mono-meta text-[11px] uppercase tracking-[0.18em] text-[#8C8983] pt-0.5">{k}</dt>
                <dd className="text-sm text-right">{v}</dd>
              </div>
            ))}
            <div className="flex justify-between gap-6 py-4 border-b" style={{ borderColor: "#E5E2DC" }}>
              <dt className="font-mono-meta text-[11px] uppercase tracking-[0.18em] text-[#8C8983] pt-0.5">{t("detail.status")}</dt>
              <dd
                className="font-mono-meta text-[11px] uppercase tracking-[0.15em]"
                style={{ color: w.status === "available" ? "#121212" : "#C83E2B" }}
                data-testid="detail-status"
              >
                {t(statusKey(w.status))}
              </dd>
            </div>
          </dl>

          {w.status !== "sold" && (
            <div className="flex items-baseline justify-between mt-8 mb-2">
              <span className="font-mono-meta text-[11px] uppercase tracking-[0.18em] text-[#8C8983]">{t("detail.price")}</span>
              <span className="font-serif-display text-4xl" data-testid="detail-price">
                {fmtPrice(w.price_eur, lang)}
              </span>
            </div>
          )}
          <p className="flex items-center gap-2 text-xs text-[#8C8983] mb-8 mt-6">
            <BadgeCheck size={14} className="text-[#B39359]" />
            {t("detail.certificate")}
          </p>

          {w.status === "available" ? (
            <button
              data-testid="detail-acquire-btn"
              onClick={() => setAcquire(true)}
              className="w-full py-5 font-mono-meta text-xs uppercase tracking-[0.22em] text-[#FAF9F6] hover:opacity-85 transition-opacity"
              style={{ background: "#C83E2B" }}
            >
              {t("detail.acquire")}
            </button>
          ) : (
            <p className="py-5 text-center font-mono-meta text-xs uppercase tracking-[0.18em] text-[#8C8983] border border-dashed" style={{ borderColor: "#C5C0B7" }} data-testid="detail-sold-note">
              {t("detail.sold_note")}
            </p>
          )}

          <div className="flex justify-between mt-12 pt-6 border-t" style={{ borderColor: "#E5E2DC" }}>
            <Link
              to={`/oeuvres/${data.prev_slug}`}
              data-testid="detail-prev-link"
              className="inline-flex items-center gap-2 font-mono-meta text-[11px] uppercase tracking-[0.18em] link-line"
            >
              <ArrowLeft size={13} /> {t("detail.prev")}
            </Link>
            <Link
              to={`/oeuvres/${data.next_slug}`}
              data-testid="detail-next-link"
              className="inline-flex items-center gap-2 font-mono-meta text-[11px] uppercase tracking-[0.18em] link-line"
            >
              {t("detail.next")} <ArrowRight size={13} />
            </Link>
          </div>
        </motion.aside>
      </div>

      <AcquirePanel artwork={w} open={acquire} onClose={() => setAcquire(false)} />
    </div>
  );
}
